import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from 'components/editor/grid/reduxStore/Store';
import { useAppDispatch } from 'components/editor/grid/reduxStore/Store';

import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import IcoLock from 'components/icon/icons/IcoLock';
import IcoLockOpen from 'components/icon/icons/IcoLockOpen';

import { EditorDefaultSidePanelType, setActiveSidePanel } from 'components/editor/grid/reduxStore/blockStyleSettingsSlice';
import type { BlockContent } from 'components/editor/grid/reduxStore/editorSlice';
import { selectSectionIdByBlockId } from 'components/editor/grid/reduxStore/editorSlice';

import { SecondaryMenu, SecondaryMenuSectionTitle, NumberInputBoxSingleCol } from 'components/editor/SidePanel/component';

import { useSelectionContext } from 'components/editor/GridDndEditor/SelectedBlockInfoProvider';
import { useBlockContentWithSectionId } from 'components/editor/Sections/useBlockContent';
import { useEmbedWithUndoRedoBlockSettings } from 'components/editor/hooks/useEmbedWithUndoRedoBlockSettings';
import EmbedDialog from 'components/editor/embed/EmbedDialog';
import EmbedProviderButtons from 'components/editor/embed/EmbedProviderButtons';
import { useEmbedDialogState } from 'components/editor/embed/useEmbedDialogState';
import {
  EMBED_PROVIDER_CONFIG,
  toEmbedDimensionChange,
  type EmbedContent,
  type ParsedEmbedCode,
  type EmbedSubtype,
} from 'components/editor/embed/embed';
import { useNewBlockDimensionChangedHandler } from 'components/editor/hooks/useNewBlockDimensionChangedHandler';
import { DIMENSION_WIDTH, DIMENSION_HEIGHT } from 'components/editor/SidePanel/content/DesignSettings/common/useAspectRatio';
import { useNewAspectRatio } from 'components/editor/SidePanel/content/DesignSettings/common/useNewAspectRatio.ts';

export function EmbedWithUndoRedoBlockSettings() {
  const { selectedBlockIdByWrapper } = useSelectionContext();
  const blockId = selectedBlockIdByWrapper as string;
  const sectionId = useSelector((state: RootState) => selectSectionIdByBlockId(state, blockId));
  const blockContent = useBlockContentWithSectionId(sectionId ?? '', blockId);

  if (!blockContent) {
    return null;
  }

  return <EmbedBlockSettingsContent key={blockId} blockContent={blockContent} blockId={blockId} sectionId={sectionId as string} />;
}

const EmbedBlockSettingsContent = ({
  blockContent,
  blockId,
  sectionId,
}: {
  blockContent: BlockContent;
  blockId: string;
  sectionId: string;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { updateEmbedContent } = useEmbedWithUndoRedoBlockSettings();
  const embedContent = { embedUrl: '', aspectRatioLocked: true, ...blockContent?.contentObject } as EmbedContent;
  const { openSubtype, openDialog, closeDialog } = useEmbedDialogState(blockId);
  const blockDimensionChanged = useNewBlockDimensionChangedHandler();

  const { handleImageDimensionChange } = useNewAspectRatio({
    imageAspectRatioLock: embedContent.aspectRatioLocked ?? true,
    sectionId: sectionId ?? '',
    blockId,
  });

  const handleChangeLinkSubmit = (embedUrl: string, subtype: EmbedSubtype, dimensions: ParsedEmbedCode['dimensions']) => {
    updateEmbedContent({ ...embedContent, embedUrl, subtype, validationStatus: undefined });
    closeDialog();

    if (dimensions) {
      blockDimensionChanged(sectionId, blockId, ...toEmbedDimensionChange(blockContent.blockConfig, dimensions));
    }
  };

  const width = blockContent?.blockConfig.width || 0;
  const height = blockContent?.blockConfig.height || 0;

  const [typedWidth, setTypedWidth] = useState<number | null>(null);
  const [typedHeight, setTypedHeight] = useState<number | null>(null);

  const originalDimensionsRef = useRef<{
    width: number;
    height: number;
    blockId: string;
    blockContent: BlockContent;
  } | null>(null);

  useEffect(() => {
    setTypedWidth(null);
    setTypedHeight(null);
    originalDimensionsRef.current = null;
  }, [blockId]);

  const handleDimensionFocus = useCallback(() => {
    originalDimensionsRef.current = {
      width,
      height,
      blockId,
      blockContent: {
        ...blockContent,
        blockConfig: { ...blockContent.blockConfig },
      },
    };
  }, [width, height, blockId, blockContent]);

  const handleWidthChange = (newWidth: number) => {
    setTypedWidth(newWidth);

    if (originalDimensionsRef.current) {
      handleImageDimensionChange(DIMENSION_WIDTH, newWidth, false, originalDimensionsRef.current);
    }
  };

  const handleWidthBlur = () => {
    if (typedWidth !== null && originalDimensionsRef.current) {
      handleImageDimensionChange(DIMENSION_WIDTH, typedWidth, true, originalDimensionsRef.current);
    }

    setTypedWidth(null);
  };

  const handleHeightChange = (newHeight: number) => {
    setTypedHeight(newHeight);

    if (originalDimensionsRef.current) {
      handleImageDimensionChange(DIMENSION_HEIGHT, newHeight, false, originalDimensionsRef.current);
    }
  };

  const handleHeightBlur = () => {
    if (typedHeight !== null && originalDimensionsRef.current) {
      handleImageDimensionChange(DIMENSION_HEIGHT, typedHeight, true, originalDimensionsRef.current);
    }

    setTypedHeight(null);
  };

  const handleBackButtonClick = useCallback(() => {
    dispatch(setActiveSidePanel({ type: EditorDefaultSidePanelType.EDITOR_DEFAULT_SIDE_PANEL }));
  }, [dispatch]);

  const providerName = embedContent.subtype ? EMBED_PROVIDER_CONFIG[embedContent.subtype].providerName : undefined;
  const menuTitle = providerName
    ? t('document.grid.embed.design.title', { provider: providerName })
    : t('document.grid.embed.design.title_generic');

  return (
    <SecondaryMenu testId="embed-block-settings-component" menuTitle={menuTitle} backButtonOnClick={handleBackButtonClick}>
      <Stack rowGap="16px" width="100%">
        <SecondaryMenuSectionTitle hasUnderLine={true}>{t('document.grid.embed.design.general')}</SecondaryMenuSectionTitle>

        <EmbedProviderButtons onSelectProvider={openDialog} />

        {embedContent.embedUrl && openSubtype && (
          <EmbedDialog open subtype={openSubtype} embedContent={embedContent} onClose={closeDialog} onSubmit={handleChangeLinkSubmit} />
        )}

        <SecondaryMenuSectionTitle hasUnderLine={true}>{t('document.grid.embed.design.display')}</SecondaryMenuSectionTitle>

        <Stack gap={1} direction="row" alignItems="end">
          <Stack direction="row" gap={2}>
            <NumberInputBoxSingleCol
              title={t('document.grid.embed.design.width')}
              onChange={handleWidthChange}
              onBlur={handleWidthBlur}
              onFocus={handleDimensionFocus}
              value={width}
              endAdornmentText="px"
              testId="embed-width-input"
            />
            <NumberInputBoxSingleCol
              title={t('document.grid.embed.design.height')}
              onChange={handleHeightChange}
              onBlur={handleHeightBlur}
              onFocus={handleDimensionFocus}
              value={height}
              endAdornmentText="px"
              testId="embed-height-input"
            />
          </Stack>

          <IconButton
            data-testid="aspect-ratio-lock-button"
            onClick={() => updateEmbedContent({ ...embedContent, aspectRatioLocked: !embedContent.aspectRatioLocked })}
          >
            {embedContent.aspectRatioLocked ? <IcoLock /> : <IcoLockOpen />}
          </IconButton>
        </Stack>
      </Stack>
    </SecondaryMenu>
  );
};
